import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Space, Typography, Tag } from 'antd';
import {
    CheckCircleOutlined,
    EditOutlined,
    ExclamationCircleOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';


const SimplifiedInterestAlert = ({ interests }) => {
    const navigate = useNavigate();
    const [isExpanded, setIsExpanded] = useState(false);

    const handleEditInterests = () => {
        navigate('/personal-interest');
    };


    // Extract all subcategory names from the nested structure
    const selectedInterests = interests.flatMap(category =>
        category.subCategories ? category.subCategories.map(sub => sub.name) : []
    );

    const totalSelections = selectedInterests.length;
    const hasInterests = totalSelections > 0;

    // If no interests, show a compact alert
    if (!hasInterests) {
        return (
            <div style={{
                background: '#fff7e6', // Light orange background for warning
                border: '1px solid #ffd591',
                borderRadius: '8px',
                padding: '16px',
                marginBottom: '24px',
                boxShadow: '0 2px 8px rgba(250, 140, 22, 0.1)'
            }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <ExclamationCircleOutlined
                            style={{ fontSize: '20px', color: '#fa8c16', flexShrink: 0 }}
                        />
                        <div>
                            <div style={{
                                fontSize: '14px',
                                fontWeight: '600',
                                color: '#000'
                            }}>
                                No Focus Areas Selected
                            </div>
                        </div>
                    </div>
                    <Button
                        type="primary"
                        size="small"
                        onClick={handleEditInterests}
                        style={{ borderRadius: '6px', fontWeight: '500' }}
                        icon={<EditOutlined />}
                    >
                        Select Interests
                    </Button>
                </div>
            </div>
        );
    }

    // If interests exist, show the compact alert
    return (
        <div style={{
            background: '#e6f7ff', // Light blue background for an alert/info feel
            border: '1px solid #91d5ff',
            borderRadius: '12px',
            padding: '16px',
            marginBottom: '24px',
            boxShadow: '0 2px 8px rgba(24, 144, 255, 0.1)'
        }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <CheckCircleOutlined
                        style={{ fontSize: '20px', color: '#1890ff', flexShrink: 0 }}
                    />
                    <div>
                        <div style={{
                            fontSize: '14px',
                            fontWeight: '600',
                            color: '#000'
                        }}>
                            🎯 Your Focus Areas ({totalSelections} Selected)
                        </div>
                        {isExpanded && (
                            <div style={{
                                fontSize: '12px',
                                color: '#595959',
                                marginTop: '8px',
                                marginBottom: '12px'
                            }}>
                                These interests shape your personalized learning recommendations.
                            </div>
                        )}
                    </div>
                </div>

                <div style={{ display: 'flex', gap: '8px' }}>
                    <Button
                        type="text"
                        size="small"
                        onClick={() => setIsExpanded(!isExpanded)}
                        style={{
                            fontWeight: '500',
                            color: '#1890ff',
                            padding: '0 8px'
                        }}
                    >
                        {isExpanded ? 'Show Less' : 'Show More'}
                    </Button>
                    <Button
                        type="default"
                        size="small"
                        onClick={handleEditInterests}
                        style={{ borderRadius: '6px', fontWeight: '500' }}
                        icon={<EditOutlined />}
                    >
                        Edit
                    </Button>
                </div>
            </div>

            {/* Expanded content - only show when expanded */}
            {isExpanded && (
                <>
                    {/* Interest Tags (Subcategories) */}
                    <div style={{ marginTop: '16px', marginBottom: '16px' }}>
                        <Space wrap size={[8, 8]}>
                            {selectedInterests.map((interestName, index) => (
                                <Tag
                                    key={index}
                                    color="blue"
                                    style={{
                                        borderRadius: '16px',
                                        padding: '4px 10px',
                                        fontSize: '12px',
                                        fontWeight: '500',
                                        background: '#f0f7ff',
                                        borderColor: '#91d5ff',
                                    }}
                                >
                                    {interestName}
                                </Tag>
                            ))}
                        </Space>
                    </div>


                </>
            )}
        </div>
    );
};

SimplifiedInterestAlert.propTypes = {
    interests: PropTypes.arrayOf(
        PropTypes.shape({
            subCategories: PropTypes.arrayOf(
                PropTypes.shape({
                    name: PropTypes.string.isRequired,
                })
            )
        })
    ).isRequired
};

SimplifiedInterestAlert.defaultProps = {
    interests: []
};

export default SimplifiedInterestAlert;